import React from "react"
import PropTypes from "prop-types"
import FontAwesome from "react-fontawesome"
import moment from "moment"

import ResultCount from "../ResultCount"
import Loading from "../Loading"

import "./LostAssetsReport.scss"
import { latestUpdatedTime } from "../../helpers/searchAssets"
import { timeBetween } from "../../helpers/sharedHelperFunctions"
import LostAssetsTableVirtualized from "./LostAssetsTableVirtualized"
import DepartmentDropdownWithData from "../DepartmentDropdown/DepartmentDropdownWithData"
import CSVButton from "@swipesense/csv-button"
import SearchBox from "../SearchBox"

const sortLostAssets = (assetA, assetB) => {
  if (assetA.item.lastSeenAt > assetB.item.lastSeenAt) {
    return -1
  }
  if (assetA.item.lastSeenAt < assetB.item.lastSeenAt) {
    return 1
  }
  return 0
}

const determineLostAssets = assets => {
  const now = Date.now()
  return assets.filter(asset => {
    if (!asset.lastSeenAt || !asset.location) {
      return false
    }
    const timeSinceDetected = timeBetween(Date.parse(asset.lastSeenAt), now)
    // 7 days in ms
    return timeSinceDetected > 604800000
  })
}

const csvHeaders = [
  { label: "Asset", key: "name" },
  { label: "Manufacturer", key: "manufacturer" },
  { label: "Model", key: "modelName" },
  { label: "Asset ID", key: "assetId" },
  { label: "Serial Number", key: "serialNumber" },
  { label: "Department", key: "ownerNames" },
  { label: "Ownership", key: "ownership" },
  { label: "Last Location", key: "locationName" },
  { label: "Time Lost", key: "timeLost" },
]

const csvDataMap = lost => ({
  ...lost.item,
  locationName: [lost.item.location.unit.name, lost.item.location.name].join(
    " "
  ),
  ownerNames: lost.item.owners.map(owner => owner.name.trim()).join(" and "),
  timeLost: moment(lost.item.lastSeenAt).format("HH:MM MM/DD/YYYY"),
})

class LostAssetsReport extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      filterSearch: "",
      filterToRentals: false,
      filteredDept: null,
    }
  }

  searchableKeys = [
    "name",
    "assetId",
    "labels",
    "manufacturer",
    "modelName",
    "serialNumber",
    "location.unit.name",
    "location.name",
  ]

  searchLostAssets = assets => {
    if (!this.state.filterSearch) {
      return assets.map(asset => ({
        item: asset,
      }))
    }

    const searchTerms = this.state.filterSearch.toLowerCase()

    // Return any asset that has at least one of the searchableKeys containing the search term as a substring.
    return assets
      .filter(asset =>
        this.searchableKeys.some(
          key =>
            key
              .split(".")
              .reduce((o, subkey) => o[subkey] || "", asset)
              .toLowerCase()
              .indexOf(searchTerms) !== -1
        )
      )
      .map(asset => ({ item: asset }))
  }

  updateDepartment = departmentSelection => {
    this.setState({
      filteredDept: departmentSelection,
    })
  }

  render() {
    const formattedUpdateTime = this.props.loading
      ? null
      : latestUpdatedTime(this.props.allAssets)

    const availableAssets = this.props.allAssets
      .filter(
        asset => !this.state.filterToRentals || asset.ownership === "rented"
      )
      .filter(
        asset =>
          this.state.filteredDept == null ||
          this.state.filteredDept.value == null ||
          asset.owners.map(o => o.id).includes(this.state.filteredDept.value)
      )
    const lostAssets = this.searchLostAssets(
      determineLostAssets(availableAssets)
    ).sort(sortLostAssets)

    const csvFileName = `SwipeSense Lost Asset Report ${
      this.state.filteredDept ? this.state.filteredDept.label.trim() + " " : ""
    }${
      this.state.filterToRentals ? "Rentals " : ""
    }${new Date().toLocaleDateString()}.csv`

    return (
      <div className="lost-assets-report-container">
        <div>
          {this.props.loading ? (
            <Loading />
          ) : (
            <div className="LostAssetsReport">
              <div className="lost-top-bar">
                <div className="lost-summary">
                  INVENTORY LOST
                  <div className="inventory-numbers">
                    <div className="inventory-percent">
                      <b>
                        {Math.round(
                          (lostAssets.length / availableAssets.length) * 100
                        ) || 0}
                        %
                      </b>
                    </div>
                    <div className="inventory-fraction">
                      ({lostAssets.length}/{availableAssets.length})
                    </div>
                  </div>
                </div>
                <div className="filter-options">
                  <label className="rentals-only" htmlFor="onlyRented">
                    <input
                      type="checkbox"
                      onChange={event => {
                        this.setState({
                          filterToRentals: event.target.checked,
                        })
                      }}
                      name={"onlyRented"}
                      id={"onlyRented"}
                      checked={this.state.filterToRentals}
                    />
                    <FontAwesome className="rental-tag" name={"tag"} />
                    Rentals only
                  </label>
                  <DepartmentDropdownWithData
                    className="department-dropdown"
                    facilityId={this.props.facilityId}
                    departmentUpdated={this.updateDepartment}
                  />
                </div>
              </div>
              <div className="report-controls">
                <ResultCount
                  resultCount={lostAssets.length}
                  deviceType={this.props.deviceType}
                  loading={this.props.loading}
                  updateTime={formattedUpdateTime}
                />
                <div className="search-download">
                  <SearchBox
                    query={this.state.filterSearch}
                    onChange={query => this.setState({ filterSearch: query })}
                  />
                  <CSVButton
                    data={lostAssets}
                    filename={csvFileName}
                    headers={csvHeaders}
                    mapData={csvDataMap}
                  />
                </div>
              </div>
              <div className="results">
                <LostAssetsTableVirtualized
                  lostAssets={lostAssets}
                  deviceType={this.props.deviceType}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

LostAssetsReport.propTypes = {
  facilityId: PropTypes.string.isRequired,
  allAssets: PropTypes.array,
  loading: PropTypes.bool,
  deviceType: PropTypes.string.isRequired,
}

LostAssetsReport.defaultProps = {
  allAssets: [],
  deviceType: "desktop",
}

export default LostAssetsReport
